import { GroupBillingFields } from '@/components/group-billing-fields';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { MultiLanguageInput } from '@/components/ui/multi-language-input';
import { Skeleton } from '@/components/ui/skeleton';
import { useGroup, useUpdateGroup } from '@/hooks/use-groups';
import { getLocalizedText } from '@/lib/utils';
import { createGroupSchema, type GroupFormData } from '@fame/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft } from 'lucide-react';
import { useEffect } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';

export function EditGroupPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: group, isLoading } = useGroup(id!);
  const updateGroup = useUpdateGroup();

  const form = useForm<GroupFormData>({
    resolver: zodResolver(createGroupSchema) as Resolver<GroupFormData>,
    defaultValues: {
      projectId: '',
      name: { nl: '', fr: '', en: '' },
      packagePrice: 0,
      singleRoomSupplement: undefined,
      invoiceDescription: undefined,
      depositDescription: undefined,
      invoiceRemark: undefined,
      invoiceAdjustments: [],
      isActive: true,
    },
  });

  useEffect(() => {
    if (group) {
      const normalizedInvoiceAdjustments = (group.invoiceAdjustments ?? []).map(adjustment => ({
        ...adjustment,
        description:
          typeof adjustment.description === 'string'
            ? { nl: adjustment.description, fr: '', en: '' }
            : adjustment.description,
      }));

      form.reset({
        projectId: group.projectId,
        name: group.name,
        packagePrice: Number(group.packagePrice) || 0,
        singleRoomSupplement: group.singleRoomSupplement ? Number(group.singleRoomSupplement) : undefined,
        invoiceDescription: group.invoiceDescription ?? undefined,
        depositDescription: group.depositDescription ?? undefined,
        invoiceRemark: group.invoiceRemark ?? undefined,
        invoiceAdjustments: normalizedInvoiceAdjustments,
        isActive: group.isActive,
      });
    }
  }, [group, form]);

  const onSubmit = async (data: GroupFormData) => {
    await updateGroup.mutateAsync({ id: id!, data });
    navigate(-1); // Go back to previous page
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!group) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Groep niet gevonden</p>
        <Button asChild variant="link">
          <Link to="/groepen">Terug naar groepen</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Groep Bewerken</h1>
          <p className="text-muted-foreground">{getLocalizedText(group.name)}</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Groep Informatie</CardTitle>
          <CardDescription>Bewerk de gegevens van deze reisgroep</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Groepsnaam</FormLabel>
                    <FormControl>
                      <MultiLanguageInput placeholder="bijv. Wave 1" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <GroupBillingFields control={form.control} />

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                  Annuleren
                </Button>
                <Button type="submit" disabled={updateGroup.isPending}>
                  {updateGroup.isPending ? 'Bezig met opslaan...' : 'Opslaan'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
